// grok-orders-20261021: runs ONE armed Grok recipe. Compiled to closures at
// arm time; evaluated on every signalBus event (trade / liq / ard) on the
// same call stack, so the order call goes out right on the triggering event.
// Ready conditions only gate; the trigger fires. One shot.
import { useState, useRef, useEffect, useCallback } from 'react'
import { subscribeSignals } from '../../utils/signalBus'
import { getRecipe, compileRecipe } from '../../utils/orderRecipes'

const ARM_TIMEOUT_MS = 30 * 60 * 1000

export function useRecipeOrder({ trading, currentToken, publicKey, buyAmountSol, maxSol, pushOrder, isFutures, showToast, onEnd }) {
  const [status, setStatus] = useState(null)       // { phase: 'armed'|'firing'|'done', text }
  const runRef = useRef(null)
  const ctxRef = useRef({})
  ctxRef.current = { trading, currentToken, publicKey, buyAmountSol, maxSol, pushOrder, isFutures, showToast, onEnd }

  // order-chain-20261021: ok = action fired; handoff = Push took over (Push's own end reports).
  const stop = useCallback((why, ok, handoff) => {
    const r = runRef.current; if (!r) return
    runRef.current = null; r.dead = true
    try { r.off?.() } catch {}
    try { clearTimeout(r.to) } catch {}
    setStatus(why ? { phase: 'done', text: why } : null)
    if (!handoff) { try { ctxRef.current.onEnd?.(!!ok) } catch {} }
  }, [])

  const fire = async (r) => {
    const { trading, pushOrder, showToast } = ctxRef.current
    try {
      setStatus({ phase: 'firing', text: `${r.label}: firing` })
      if (r.kind === 'pushExit') {
        stop(null, false, true)
        pushOrder.arm({ sol: r.sol })          // Push does the buy + breakeven exit
        if (pushOrder.running && !pushOrder.running()) { try { ctxRef.current.onEnd?.(false) } catch {} }   // Push refused to arm
        return
      }
      if (r.kind === 'buy') await trading.executeMarketOrder('buy', { buyAmountSol: r.sol, token: r.token })
      else await trading.executeMarketOrder('sell', { sellFraction: r.pct / 100, token: r.token })
      showToast?.(`${r.label}: ${r.kind === 'buy' ? `bought ${r.sol} SOL` : `sold ${r.pct}%`}`, r.kind)
      stop(`${r.label}: done`, true)
    } catch (e) {
      showToast?.(`${r.label} failed: ${e?.message || e}`, 'sell')
      stop(`${r.label}: failed`)
    }
  }

  const arm = useCallback((id) => {
    const { currentToken, publicKey, buyAmountSol, maxSol, pushOrder, isFutures, showToast } = ctxRef.current
    try {
      if (runRef.current) { const l = runRef.current.label; stop(); showToast?.(`${l} cancelled`, 'sell'); return }
      if (isFutures) return showToast?.('Recipes are Solana only', 'sell')
      if (!currentToken) return showToast?.('Select a token first', 'sell')
      const recipe = getRecipe(id); if (!recipe) return showToast?.('Order not found', 'sell')
      const a = recipe.action || {}
      const r = { id, label: recipe.label, token: currentToken, me: publicKey ? publicKey.toString() : '', firing: false, dead: false, wasReady: null }
      if (a.buy != null) {
        r.sol = a.buy === 'default' ? Number(buyAmountSol) || 0 : Number(a.buy)
        if (!(r.sol > 0)) return showToast?.('Set a buy size first', 'sell')
        if (r.sol > maxSol) return showToast?.(`${recipe.label} refused: ${r.sol} SOL > max ${maxSol} SOL`, 'sell')
        r.kind = recipe.then === 'pushExit' ? 'pushExit' : 'buy'
        if (r.kind === 'pushExit' && pushOrder?.armed) return showToast?.('A Push is already running', 'sell')
      } else { r.kind = 'sell'; r.pct = Number(a.sell) }
      const { ready, trigger } = compileRecipe(recipe)   // closures, settings fixed now
      r.off = subscribeSignals((ev) => {
        if (r.dead || r.firing || ev.token !== r.token) return
        if (!trigger(ev, r.me)) {
          // cheap ready-state display, only on change and only on trades
          if (ev.kind === 'trade') {
            const ok = ready()
            if (ok !== r.wasReady) { r.wasReady = ok; setStatus({ phase: 'armed', text: `${r.label}: ${ok ? 'ready' : 'waiting ready'}` }) }
          }
          return
        }
        if (!ready()) return
        r.firing = true                          // sync guard before any await
        fire(r)
      })
      r.to = setTimeout(() => stop(`${r.label}: expired`), ARM_TIMEOUT_MS)
      runRef.current = r
      setStatus({ phase: 'armed', text: `${r.label}: armed` })
      showToast?.(`${recipe.label} armed`, 'buy')
    } catch (e) {
      try { console.warn('[Recipe] arm', e) } catch {}
      stop('error')
    }
  }, [stop]) // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = useCallback(() => { const r = runRef.current; if (r) stop(`${r.label}: cancelled`); else setStatus(null) }, [stop])

  useEffect(() => {
    const r = runRef.current
    if (r && currentToken && currentToken !== r.token) stop(`${r.label}: cancelled - token changed`)
  }, [currentToken, stop])
  useEffect(() => () => { try { stop() } catch {} }, [stop])

  return { status, armed: !!runRef.current, running: () => !!runRef.current, arm, cancel, clear: () => setStatus(null) }
}
